import { gql } from '@apollo/client';
import {
  Box,
  Grid,
  GridItem,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { PropsWithChildren } from 'react';
import { CellProps } from 'react-table';
import { useAuth } from '~auth/useAuth';
import { ExportConfigurationModal } from '~components/configurations/ExportConfigurationModal';
import { UnAssignConfigurationModal } from '~components/configurations/UnAssignConfigurationModal';
import { BulkActionModal_DisplayFragment } from '~components/displays/BulkAction/__generated__/BulkActionModal.graphql';
import { BulkAssignConfigurationInner } from '~components/displays/BulkAction/BulkAssignConfiguration';
import { DisplaySyncStatus } from '~components/displays/DisplaySyncStatus';
import { ConfigurationSyncStatus } from '~graphql/__generated__/types';
import { DisplayTable_DisplayFragment } from '../__generated__/DisplayTableServerSide.graphql';
import { ConfigurationCell_DisplayFragment } from './__generated__/ConfigurationCell.graphql';

export function ConfigurationCell({
  value,
}: PropsWithChildren<
  CellProps<
    DisplayTable_DisplayFragment,
    ConfigurationCell_DisplayFragment & BulkActionModal_DisplayFragment
  >
>) {
  const { verifyUserPermissions } = useAuth();
  const hasConfigurationUpdatePermission = verifyUserPermissions([Permission.ConfigurationUpdate]);
  const createConfigurationModal = useDisclosure();
  const unassignConfigurationModal = useDisclosure();
  const assignConfigurationModal = useDisclosure();
  const isSyncing = value.configurationSyncStatus === ConfigurationSyncStatus.Syncing;

  return (
    <>
      {/* <Tooltip label={value.configuration?.name}> */}
      <Grid templateColumns="1fr auto" alignItems="center" gap={2}>
        <GridItem>
          <HStack gap={2}>
            <Box
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              color={'gray.500'}
              fontWeight="semibold"
              maxW={150}
            >
              {value.configuration?.name}
            </Box>
            {value.configurationSyncStatus && (
              <Box maxW={'200px'}>
                <DisplaySyncStatus syncStatus={value.configurationSyncStatus} />
              </Box>
            )}
          </HStack>
        </GridItem>

        <GridItem>
          <Menu placement="bottom-end">
            <MenuButton
              as={Box}
              cursor="pointer"
              p={2}
              _hover={{ bg: 'gray.100', borderRadius: 'md' }}
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                gap="2px"
                height="24px"
                width="16px"
              >
                <Box w="3px" h="3px" bg="gray.600" borderRadius="full" />
                <Box w="3px" h="3px" bg="gray.600" borderRadius="full" />
                <Box w="3px" h="3px" bg="gray.600" borderRadius="full" />
              </Box>
            </MenuButton>

            <MenuList p={0.3}>
              <MenuItem
                p={4}
                fontWeight={'semibold'}
                textColor={'grey.500'}
                isDisabled={isSyncing || !hasConfigurationUpdatePermission}
                onClick={(e) => {
                  assignConfigurationModal.onOpen();
                  e.stopPropagation();
                }}
              >
                {t('assignNewConfiguration')}
              </MenuItem>
              <MenuItem
                p={4}
                fontWeight={'semibold'}
                textColor={'grey.400'}
                isDisabled={!value.configuration?.id || !hasConfigurationUpdatePermission}
                onClick={(e) => {
                  unassignConfigurationModal.onOpen();
                  e.stopPropagation();
                }}
              >
                {t('unassignConfiguration')}
              </MenuItem>
              <MenuItem
                p={4}
                fontWeight={'semibold'}
                textColor={'grey.300'}
                isDisabled={isSyncing || !hasConfigurationUpdatePermission}
                onClick={(e) => {
                  createConfigurationModal.onOpen();
                  e.stopPropagation();
                }}
              >
                {t('ImportConfiguration')}
              </MenuItem>
            </MenuList>
          </Menu>
        </GridItem>
      </Grid>
      {/* </Tooltip> */}
      <ExportConfigurationModal
        displayId={value.id}
        isOpen={createConfigurationModal.isOpen}
        onCancel={createConfigurationModal.onClose}
        defaultName={value.alias + '_config'}
      />
      <UnAssignConfigurationModal
        displayId={value.id}
        isOpen={unassignConfigurationModal.isOpen}
        onCancel={unassignConfigurationModal.onClose}
        configurationId={value.configuration?.id}
      />
      {assignConfigurationModal.isOpen && (
        <Modal isOpen={assignConfigurationModal.isOpen} onClose={assignConfigurationModal.onClose}>
          <ModalOverlay />
          <BulkAssignConfigurationInner
            displays={[value]}
            onCancel={assignConfigurationModal.onClose}
            onBack={assignConfigurationModal.onClose}
            onSuccess={assignConfigurationModal.onClose}
            hideBackButton={true}
          />
        </Modal>
      )}
    </>
  );
}

ConfigurationCell.graphql = {
  fragments: {
    ConfigurationCell_display: gql`
      fragment ConfigurationCell_display on Display {
        id
        alias
        configuration {
          id
          name
        }
        configurationSyncStatus
      }
    `,
  },
};
